/**
 * Calibrator Table Component
 * Data table with sorting, pagination, and actions
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Pencil,
  Trash2,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import type { Calibrator } from '../types';
import { CALIBRATOR_TYPES } from '../types';

// =============================================================================
// Column Config
// =============================================================================

interface ColumnConfig {
  key: string;
  label: string;
  sortKey?: string;
  align?: 'left' | 'center' | 'right';
  render: (calibrator: Calibrator) => React.ReactNode;
}

const COLUMNS: ColumnConfig[] = [
  {
    key: 'name',
    label: 'Name',
    sortKey: 'name',
    render: (c) => <span className="font-medium">{c.name}</span>,
  },
  {
    key: 'type',
    label: 'Type',
    render: (c) => (
      <span className="text-sm">
        {CALIBRATOR_TYPES[c.type] || `Type ${c.type}`}
      </span>
    ),
  },
  {
    key: 'manufacturer',
    label: 'Manufacturer',
    sortKey: 'manufacturer',
    render: (c) =>
      c.manufacturer ? (
        <span className="text-sm">{c.manufacturer}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'model',
    label: 'Model',
    render: (c) =>
      c.model ? (
        <span className="text-sm">{c.model}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'serialNumber',
    label: 'Serial Number',
    render: (c) =>
      c.serialNumber ? (
        <span className="font-mono text-xs">{c.serialNumber}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'unit',
    label: 'Unit',
    render: (c) => <span className="text-sm">{c.unit}</span>,
  },
  {
    key: 'certNumber',
    label: 'Cert #',
    render: (c) => <span className="text-sm">{c.certNumber}</span>,
  },
  {
    key: 'certDueDate',
    label: 'Cert Expiry',
    sortKey: 'cert_due_date',
    render: (c) => {
      const dateStr = formatDate(c.certDueDate);
      const isExpired =
        c.certDueDate && new Date(c.certDueDate) < new Date();
      return (
        <span className={cn('text-sm', isExpired && 'text-red-600 font-medium')}>
          {dateStr}
        </span>
      );
    },
  },
  {
    key: 'status',
    label: 'Status',
    align: 'center',
    render: (c) =>
      c.enabled ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          Enabled
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Disabled
        </span>
      ),
  },
];

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: number;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Table Component
// =============================================================================

interface CalibratorTableProps {
  calibrators: Calibrator[];
  total: number;
  page: number;
  pageLength: number;
  sortKey: string;
  sortDirection: number;
  onPageChange: (page: number) => void;
  onSortChange: (key: string, direction: number) => void;
  onEdit: (calibrator: Calibrator) => void;
  onDelete: (calibrator: Calibrator) => void;
  isLoading: boolean;
}

export function CalibratorTable({
  calibrators,
  total,
  page,
  pageLength,
  sortKey,
  sortDirection,
  onPageChange,
  onSortChange,
  onEdit,
  onDelete,
  isLoading,
}: CalibratorTableProps) {
  const totalPages = Math.ceil(total / pageLength);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      onSortChange(key, sortDirection === 1 ? -1 : 1);
    } else {
      onSortChange(key, 1);
    }
  };

  // Add actions column
  const actionsColumn: ColumnConfig = {
    key: 'actions',
    label: '',
    align: 'right',
    render: (c: Calibrator) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(c);
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(c);
            }}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ),
  };
  const allColumns: ColumnConfig[] = [...COLUMNS, actionsColumn];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Showing {calibrators.length} of {total}
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {allColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(col.align === 'right' && 'text-right', col.align === 'center' && 'text-center')}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading &&
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {allColumns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {/* Empty state */}
              {!isLoading && calibrators.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={allColumns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No calibrators found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading &&
                calibrators.map((calibrator) => (
                  <TableRow key={calibrator.id} className="hover:bg-muted/50">
                    {allColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          'text-sm',
                          col.align === 'right' && 'text-right',
                          col.align === 'center' && 'text-center',
                        )}
                      >
                        {col.render(calibrator)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
